import {useQuery} from "@apollo/client";
import keyBy from "lodash/keyBy";
import {useState} from "react";

import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Sorting} from "@/components/table/Table.types.ts";
import {useTable} from "@/lib/hooks/useTable.ts";
import {GQL_SERVICE_TAGS, ServiceTagsResponse} from "@/lib/queries/checks.ts";
import {TagsBulkActionBar} from "@/pages/tags/list/TagsBulkActionBar.tsx";
import {TagsTable} from "@/pages/tags/list/TagsTable.tsx";

import {TagsContext} from "./TagsContext";

export const TagsList = () => {
  const {search, searchDebounced, searchChanged, pagination, setPagination, sorting, setSorting, sortingString} =
    useTable({defaultSort: "tag_lower"});
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const {data, loading, previousData} = useQuery<ServiceTagsResponse>(GQL_SERVICE_TAGS, {
    variables: {
      ...pagination,
      sort: sortingString,
      search: searchDebounced,
    },
  });

  const paginationChanged = (newPagination: LimitAndOffset) => {
    setPagination(newPagination);
    setRowSelection({});
  };

  const sortingChanged = (newSorting: Sorting) => {
    setSorting(newSorting);
    setRowSelection({});
  };

  const searchInputChanged = (newSearch: string) => {
    searchChanged(newSearch);
    setRowSelection({});
  };

  const renderData = loading ? previousData : data;

  if (!renderData) {
    return <LoadingContainer text="Loading Tags..." />;
  }

  const tags = renderData.serviceTags.items;
  const tagsCount = renderData.serviceTags.total;

  return (
    <TagsContext.Provider value={keyBy(tags, "id")}>
      <SearchInput className="mb-3" variant="tableControlBar" value={search} onChange={searchInputChanged} />
      <TagsTable
        actionBar={
          <TagsBulkActionBar
            selectedIds={Object.keys(rowSelection)
              .filter((id) => rowSelection[id])
              .map((id) => tags[parseInt(id)]?.id)
              .filter((id) => !!id)}
            onSuccess={() => setRowSelection({})}
          />
        }
        tags={tags}
        sortColumn={sorting.column}
        sortDirection={sorting.direction}
        onSortingChange={sortingChanged}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        isFiltered={searchDebounced.trim().length > 0}
        pagination={pagination}
        updatePagination={paginationChanged}
        totalRows={tagsCount}
      />
    </TagsContext.Provider>
  );
};
